import React from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FlexGridRowRoot } from './styles';

/**
 * Flat vertical sortable context for FlexGridList rows (DndTable parity).
 * Nested multi-list DnD is out of scope — keep SortableList for those.
 */
export function FlexGridSortableProvider({ ids, onOrderChange, items, children, onDragActiveChange }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragStart = () => {
    onDragActiveChange && onDragActiveChange(true);
  };

  const onDragEnd = (event) => {
    onDragActiveChange && onDragActiveChange(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => String(i.id) === String(active.id));
    const newIndex = items.findIndex((i) => String(i.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onOrderChange && onOrderChange(arrayMove(items, oldIndex, newIndex));
  };

  const onDragCancel = () => {
    onDragActiveChange && onDragActiveChange(false);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/**
 * Whole-row drag target (matches DndTable). Grip icons in consumers are decorative.
 */
export const FlexGridSortableRow = React.forwardRef(function FlexGridSortableRow(
  {
    id,
    canDrag = true,
    children,
    sx,
    style,
    layout = 'block',
    dense = false,
    striped = false,
    header = false,
    ...rowProps
  },
  ref
) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canDrag,
  });

  const setRefs = (node) => {
    setNodeRef(node);
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <FlexGridRowRoot
      ref={setRefs}
      layout={layout}
      dense={dense}
      striped={striped}
      header={header}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        ...style,
      }}
      sx={{
        cursor: canDrag ? 'grab' : undefined,
        bgcolor: isDragging ? 'action.hover' : undefined,
        ...sx,
      }}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      {...rowProps}
    >
      {children}
    </FlexGridRowRoot>
  );
});
